import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import type { GoogleStatus, WhoopStatus } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';

function WhoopCard({ status, canManage }: { status: WhoopStatus; canManage: boolean }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['connections'] });
  const check = useMutation({ mutationFn: api.whoopCheck, onSettled: refresh });
  const disconnect = useMutation({ mutationFn: api.whoopDisconnect, onSettled: refresh });

  const pill = !status.configured
    ? { cls: 'pill--muted', text: 'не настроено' }
    : !status.connected
      ? { cls: 'pill--warn', text: 'не подключено' }
      : status.broken
        ? { cls: 'pill--error', text: 'нужно подключить заново' }
        : { cls: 'pill--ok', text: 'подключено' };

  const connectForm = (label: string) => (
    <form method="post" action="/connections/whoop/start">
      <button type="submit" className="btn btn--primary">
        {label}
      </button>
    </form>
  );

  return (
    <div className="card">
      <div className="card__header">
        <h3>WHOOP</h3>
        <span className={`pill ${pill.cls}`}>{pill.text}</span>
      </div>
      <p className="muted">
        Восстановление, сон, нагрузка и тренировки одного человека. Подключение общее: его делает
        владелец, данными пользуются все, у кого есть доступ.
      </p>

      {!status.configured && (
        <p className="muted">
          На сервере не заданы: <code>{status.missing.join(', ')}</code>.
        </p>
      )}

      {status.connected && (
        <dl className="facts">
          <dt>Участник</dt>
          <dd>
            {status.memberName ?? '—'}
            {status.memberId && <span className="muted"> · id {status.memberId}</span>}
          </dd>
          <dt>Подключил</dt>
          <dd>
            {status.connectedBy?.replace(/^user:/, '') ?? '—'}
            {status.connectedAt && `, ${formatShortDateTime(status.connectedAt)}`}
          </dd>
          <dt>Доступ</dt>
          <dd>{status.scopes.join(', ') || '—'}</dd>
          {status.lastError && (
            <>
              <dt>Ошибка</dt>
              <dd className="text-error">{status.lastError}</dd>
            </>
          )}
        </dl>
      )}

      {check.isError && <p className="text-error">{check.error.message}</p>}
      {check.isSuccess && !check.isPending && <p className="muted">Связь с WHOOP в порядке.</p>}
      {disconnect.isError && <p className="text-error">{disconnect.error.message}</p>}

      <div className="actions-row">
        {status.configured && canManage && !status.connected && connectForm('Привязать WHOOP')}
        {status.connected && canManage && status.broken && connectForm('Подключить заново')}
        {status.connected && !status.broken && canManage && (
          <button
            type="button"
            className="btn"
            disabled={check.isPending}
            onClick={() => check.mutate()}
          >
            {check.isPending ? 'Проверяю…' : 'Проверить связь'}
          </button>
        )}
        {status.connected && canManage && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={disconnect.isPending}
            onClick={() => {
              if (window.confirm('Отвязать WHOOP? Доступ к данным будет отозван для всех.')) {
                disconnect.mutate();
              }
            }}
          >
            Отвязать
          </button>
        )}
      </div>

      {status.configured && !canManage && !status.connected && (
        <p className="muted small">Подключить WHOOP может только владелец, войдя на сайт.</p>
      )}
      {status.configured && canManage && !status.connected && status.redirectUri && (
        <p className="muted small">
          Адрес возврата, который должен быть в приложении WHOOP: <code>{status.redirectUri}</code>
        </p>
      )}
    </div>
  );
}

function GoogleCard({ status, canManage }: { status: GoogleStatus; canManage: boolean }) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['connections'] });
  const check = useMutation({ mutationFn: api.googleCheck, onSettled: refresh });
  const unlink = useMutation({ mutationFn: api.googleUnlink, onSettled: refresh });

  const pill = !status.configured
    ? { cls: 'pill--muted', text: 'не настроено' }
    : !status.linked
      ? { cls: 'pill--warn', text: 'не привязано' }
      : status.broken
        ? { cls: 'pill--error', text: 'нужно привязать заново' }
        : { cls: 'pill--ok', text: 'привязано' };

  const linkForm = (label: string) => (
    <form method="post" action="/connections/google/start">
      <button type="submit" className="btn btn--primary">
        {label}
      </button>
    </form>
  );

  return (
    <div className="card">
      <div className="card__header">
        <h3>Google Workspace</h3>
        <span className={`pill ${pill.cls}`}>{pill.text}</span>
      </div>
      <p className="muted">
        Служебный аккаунт агента: приглашения гостям и Google Meet. Привязывает владелец, входя в
        Google именно аккаунтом агента{status.user && <> ({status.user})</>}, а не личным.
      </p>

      {!status.configured && (status.missing.length > 0 || status.invalid.length > 0) && (
        <p className="muted">
          {status.missing.length > 0 && (
            <>
              На сервере не заданы: <code>{status.missing.join(', ')}</code>.{' '}
            </>
          )}
          {status.invalid.length > 0 && (
            <>
              Неверные значения: <code>{status.invalid.join('; ')}</code>.
            </>
          )}
        </p>
      )}

      {status.linked && (
        <dl className="facts">
          <dt>Аккаунт</dt>
          <dd>{status.user ?? '—'}</dd>
          <dt>Календарь</dt>
          <dd>{status.calendarId ?? '—'}</dd>
          <dt>Привязал</dt>
          <dd>
            {status.linkedBy?.replace(/^user:/, '') ?? '—'}
            {status.linkedAt && `, ${formatShortDateTime(status.linkedAt)}`}
          </dd>
          <dt>Доступ</dt>
          <dd>{status.scopes.join(', ') || '—'}</dd>
          {status.lastError && (
            <>
              <dt>Ошибка</dt>
              <dd className="text-error">{status.lastError}</dd>
            </>
          )}
        </dl>
      )}

      {check.isError && <p className="text-error">{check.error.message}</p>}
      {check.isSuccess && !check.isPending && <p className="muted">Связь с Google в порядке.</p>}
      {unlink.isError && <p className="text-error">{unlink.error.message}</p>}

      <div className="actions-row">
        {status.configured && canManage && !status.linked && linkForm('Привязать Google')}
        {status.linked && canManage && status.broken && linkForm('Привязать заново')}
        {status.linked && !status.broken && canManage && (
          <button
            type="button"
            className="btn"
            disabled={check.isPending}
            onClick={() => check.mutate()}
          >
            {check.isPending ? 'Проверяю…' : 'Проверить связь'}
          </button>
        )}
        {status.linked && canManage && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={unlink.isPending}
            onClick={() => {
              if (
                window.confirm('Отвязать Google? Доступ агента будет отозван, гостей не позвать.')
              ) {
                unlink.mutate();
              }
            }}
          >
            Отвязать
          </button>
        )}
      </div>

      {status.configured && !canManage && !status.linked && (
        <p className="muted small">Привязать Google может только владелец, войдя на сайт.</p>
      )}
      {status.configured && canManage && !status.linked && status.redirectUri && (
        <p className="muted small">
          Адрес возврата, который должен быть в OAuth-клиенте Google:{' '}
          <code>{status.redirectUri}</code>
        </p>
      )}
    </div>
  );
}

/** External services shared by all users; a section of the Settings page. */
export function ConnectionsSection() {
  const [params] = useSearchParams();
  const query = useQuery({ queryKey: ['connections'], queryFn: api.connections });
  const connected = params.get('whoop') === 'connected';
  const linked = params.get('google') === 'linked';
  const error = params.get('whoop_error') ?? params.get('google_error');

  return (
    <section>
      <h2>Подключения</h2>
      <p className="muted">Внешние сервисы, общие для всех пользователей.</p>
      {connected && <div className="card notice notice--ok">WHOOP подключён.</div>}
      {linked && <div className="card notice notice--ok">Google привязан.</div>}
      {error && <div className="card notice notice--error">{error}</div>}
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data && !query.data.whoop && <p className="muted">Модуль WHOOP не загружен.</p>}
      {query.data?.whoop && (
        <WhoopCard status={query.data.whoop} canManage={query.data.canManage} />
      )}
      {query.data?.google && (
        <GoogleCard status={query.data.google} canManage={query.data.canManage} />
      )}
    </section>
  );
}
